import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, message, Progress } from 'antd';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import {
    useGetPortalProjectQuery,
    useGetDataGroupsQuery,
    useGetDataSourcesQuery,
    useUpdateStageStatusMutation,
} from '../../redux/api/compassPortal';
import { useGetIfUserIsAdminQuery, useGetUserTypeQuery } from '../../redux/api/user';
import StatusSelector from './StatusSelector';
import DataSourceTable from './DataSourceTable';
import DataSourceInfoModal from './modals/DataSourceInfoModal';
import ProcessingIssueModal from './modals/ProcessingIssueModal';
import ProcessedFileUploadModal from './modals/ProcessedFileUploadModal';

const DataProcessingPage = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const token = useSelector((state) => state.oauth.auth0Token);

    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [viewingSource, setViewingSource] = useState(null);
    const [issueSource, setIssueSource] = useState(null);
    const [uploadSource, setUploadSource] = useState(null);

    const { data: project, isLoading: projectLoading } = useGetPortalProjectQuery({ projectId });
    const { data: groups } = useGetDataGroupsQuery({ projectId });
    const { data: sources } = useGetDataSourcesQuery({ projectId });
    const { data: isAdmin } = useGetIfUserIsAdminQuery();
    const { data: userType } = useGetUserTypeQuery();
    const isInternal = userType === 'Internal' || userType === 'Admin';
    const [updateStageStatus] = useUpdateStageStatusMutation();

    const stage = project?.stages?.find((s) => s.id === 'data-processing');
    const collectedSources = (sources || []).filter((s) => s.collected);
    const hasOpenIssues = collectedSources.some((s) => s.processing_issue_status === 'open');

    const getSourceCount = (groupId) => collectedSources.filter((s) => s.group_id === groupId).length;

    const progressStats = useMemo(() => {
        const total = collectedSources.length;
        const processed = collectedSources.filter((s) => s.processed).length;
        const blocked = collectedSources.filter((s) => s.processing_issue_status === 'open').length;
        const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
        return { processed, total, blocked, percent };
    }, [collectedSources]);

    const groupMap = {};
    (groups || []).forEach((g) => { groupMap[g.group_id] = g.name; });

    const handleStatusChange = async (status) => {
        if (status === 'complete') {
            if (hasOpenIssues) {
                message.warning('All processing issues must be resolved first');
                return;
            }
            const allProcessed = collectedSources.length > 0 && collectedSources.every((s) => s.processed);
            if (!allProcessed) {
                message.warning('All sources must be processed first');
                return;
            }
        }
        await updateStageStatus({ projectId, stage_id: 'data-processing', status }).unwrap();
    };

    const handleDownloadOriginal = async (source) => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL || ''}/compass_portal/projects/${projectId}/sources/${source.source_id}/download`,
                { headers: { 'Authorization': `Bearer ${token}` } },
            );
            if (!res.ok) throw new Error(`Download failed: ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = source.file_name || 'download';
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            message.error('Failed to download file');
            console.error(err);
        }
    };

    const handleDownloadProcessed = async (source) => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL || ''}/compass_portal/projects/${projectId}/sources/${source.source_id}/download_processed`,
                { headers: { 'Authorization': `Bearer ${token}` } },
            );
            if (!res.ok) throw new Error(`Download failed: ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = source.processed_file_name || 'processed';
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            message.error('Failed to download processed file');
            console.error(err);
        }
    };

    const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

    if (projectLoading) {
        return <div style={{ width: '100%', textAlign: 'center', padding: '80px 0' }}><Spin size="large" /></div>;
    }

    return (
        <div style={{ width: '100%' }}>
            <button className="cp-back-link" onClick={() => navigate(`/compass-portal/${projectId}`)}>
                &larr; Back to timeline
            </button>

            <div className="cp-stage-header">
                <div className="cp-stage-header__left">
                    <h2>Data Processing</h2>
                </div>
                <div className="cp-stage-header__center">
                    <div className="cp-stage-header__dateline">
                        <span className="cp-stage-header__dateline-label">DEADLINE</span>
                        <span className="cp-stage-header__dateline-value">{stage?.due_date || '—'}</span>
                    </div>
                    <div className="cp-stage-header__dateline cp-stage-header__dateline--today">
                        <span className="cp-stage-header__dateline-label">TODAY</span>
                        <span className="cp-stage-header__dateline-value">{today}</span>
                    </div>
                </div>
                {isAdmin && (
                    <StatusSelector
                        currentStatus={stage?.status}
                        onChange={handleStatusChange}
                    />
                )}
            </div>

            <div className="cp-body">
                <div className="cp-sidebar">
                    <div className="cp-sidebar__title">Data Groups</div>
                    {(groups || []).map((group) => (
                        <div
                            key={group.group_id}
                            className={`cp-sidebar__item ${selectedGroupId === group.group_id ? 'cp-sidebar__item--active' : ''}`}
                            onClick={() => setSelectedGroupId(selectedGroupId === group.group_id ? null : group.group_id)}
                        >
                            <span className="cp-sidebar__item-name">{group.name}</span>
                            <span className="cp-sidebar__item-count">{getSourceCount(group.group_id)}</span>
                        </div>
                    ))}
                </div>

                <div className="cp-main">
                    <div className="cp-main__title-row">
                        <h3 className="cp-main__title">Data Overview</h3>
                    </div>

                    {/* Status messaging */}
                    {collectedSources.length > 0 && progressStats.percent === 100 && (
                        <div className="cp-status-banner cp-status-banner--success">
                            <CheckCircleOutlined style={{ marginRight: 8 }} />
                            All data has been processed. The team will move to validation next.
                        </div>
                    )}
                    {collectedSources.length > 0 && progressStats.percent < 100 && progressStats.blocked > 0 && (
                        <div className="cp-status-banner cp-status-banner--warning">
                            <WarningOutlined style={{ marginRight: 8 }} />
                            There's an issue with {progressStats.blocked} data source{progressStats.blocked > 1 ? 's' : ''}. Click the issue to see details and join the discussion.
                        </div>
                    )}
                    {collectedSources.length > 0 && progressStats.percent < 100 && progressStats.blocked === 0 && (
                        <div className="cp-status-banner cp-status-banner--info">
                            {progressStats.processed === 0
                                ? 'Our team is reviewing and processing your data. This page will update as each source is completed.'
                                : `Processing is underway — ${progressStats.processed} of ${progressStats.total} sources have been processed.`}
                        </div>
                    )}

                    {/* Progress bar */}
                    {collectedSources.length > 0 && progressStats.percent < 100 && (
                        <div className="cp-progress-bar">
                            <div className="cp-progress-bar__info">
                                <span>{progressStats.processed} / {progressStats.total} sources processed</span>
                            </div>
                            <Progress
                                percent={progressStats.percent}
                                strokeColor="var(--green-color)"
                                trailColor="rgba(255,255,255,0.08)"
                                showInfo={false}
                                size="small"
                            />
                        </div>
                    )}

                    <DataSourceTable
                        sources={sources}
                        groups={groups}
                        projectId={projectId}
                        mode="processing"
                        selectedGroupId={selectedGroupId}
                        onViewSource={setViewingSource}
                        onRaiseIssue={setIssueSource}
                        onUploadProcessed={setUploadSource}
                        onDownloadOriginal={handleDownloadOriginal}
                        onDownloadProcessed={handleDownloadProcessed}
                        isAdmin={isAdmin}
                        isInternal={isInternal}
                    />
                </div>
            </div>

            <DataSourceInfoModal
                open={!!viewingSource}
                onClose={() => setViewingSource(null)}
                source={viewingSource}
                projectId={projectId}
                groupName={viewingSource ? groupMap[viewingSource.group_id] : ''}
                metadataSchema={project?.metadata_schema || []}
            />
            <ProcessingIssueModal
                open={!!issueSource}
                onClose={() => setIssueSource(null)}
                source={issueSource}
                projectId={projectId}
                isAdmin={isAdmin}
                isInternal={isInternal}
            />
            <ProcessedFileUploadModal
                open={!!uploadSource}
                onClose={() => setUploadSource(null)}
                source={uploadSource}
                projectId={projectId}
            />
        </div>
    );
};

export default DataProcessingPage;
